/*
 * OGG | DIY Supply — pickup location (informational, non-restrictive).
 *
 * Tracks the customer's chosen pickup store as a convenience/preference, stored
 * as Shopify cart attributes:
 *   ogg_fulfilment_location      (display name, e.g. "Hamilton")
 *   ogg_fulfilment_location_gid  (Shopify Location GID)
 * which flow through to the order (note_attributes) for the NetSuite order sync.
 *
 * localStorage mirrors the same two values so the PDP can read the current
 * location before any cart exists / on a fresh load without a round-trip.
 *
 * BEHAVIOUR REVERTED: the store selector NO LONGER restricts the cart.
 * Switching store is non-destructive — the cart is never cleared and the page
 * never reloads; it just updates the stored preference + cart attribute and
 * broadcasts `ogg:location-changed`. Items from ANY location can be added and
 * can coexist in one cart. The PDP shows stock amounts for every location for
 * information only and never gates the add-to-cart button on location.
 *
 * CONSEQUENCE to be aware of: because a cart can now mix locations, the single
 * cart-level ogg_fulfilment_location_gid attribute only reflects the LAST store
 * the customer selected — it is no longer a reliable per-order fulfilment
 * location. If the NetSuite order sync needs an accurate fulfilment location per
 * line, that requires a per-line attribute, which this file does not set.
 *
 * ENV-SPECIFIC: the Location GIDs below are the SANDBOX gids and must match the
 * keys written into the custom.ogg_location_stock product metafield by the
 * ns-shopify-item-quantity-sync flow. Swap them for the PRODUCTION Location gids
 * at go-live (same change in buy-buttons.liquid and ogg-location-selector.liquid).
 */
(function () {
  if (window.OggLocation) return; // guard against double-load

  var EVT = 'ogg:location-changed';
  var LS_GID = 'ogg_fulfilment_location_gid';
  var LS_NAME = 'ogg_fulfilment_location';

  // gid -> display name. Keep in sync with the metafield keys + the selector snippet.
  var LOCATIONS = {
    'gid://shopify/Location/93640360192': 'Hamilton',
    'gid://shopify/Location/96961003776': 'Tauranga'
  };

  var current = null; // { gid, name } or null

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function lsGet() {
    try {
      var gid = window.localStorage.getItem(LS_GID);
      if (!gid) return null;
      return { gid: gid, name: window.localStorage.getItem(LS_NAME) || LOCATIONS[gid] || '' };
    } catch (e) {
      return null;
    }
  }

  function lsSet(gid, name) {
    try {
      window.localStorage.setItem(LS_GID, gid);
      window.localStorage.setItem(LS_NAME, name);
    } catch (e) {
      /* storage disabled — the cart attribute is still authoritative */
    }
  }

  function get() {
    return current;
  }

  function broadcast() {
    document.dispatchEvent(
      new CustomEvent(EVT, {
        detail: current ? { gid: current.gid, name: current.name } : { gid: null, name: null }
      })
    );
  }

  function writeAttributes(gid, name) {
    return fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        attributes: {
          ogg_fulfilment_location: name,
          ogg_fulfilment_location_gid: gid
        }
      })
    });
  }

  // Set the location from a user action. Returns a Promise<boolean>.
  //
  // Non-destructive: the cart is NEVER cleared and we never reload. Changing
  // store just records the preference (localStorage + cart attribute) and
  // broadcasts so the PDP availability re-renders. Items already in the cart are
  // left untouched, so a customer can add from any location.
  function set(gid) {
    var name = LOCATIONS[gid];
    if (!name) return Promise.resolve(false);

    // Same store re-selected — just re-assert in the UI.
    if (current && current.gid === gid) {
      broadcast();
      return Promise.resolve(true);
    }

    lsSet(gid, name);
    current = { gid: gid, name: name };
    broadcast();
    writeAttributes(gid, name).catch(function () {}); // best-effort preference stamp
    return Promise.resolve(true);
  }

  window.OggLocation = {
    get: get,
    set: set,
    locations: LOCATIONS,
    EVENT: EVT
  };

  /* ------------------------------------------------------------------ *
   * Header selector (non-destructive preference)
   * ------------------------------------------------------------------ */
  if (!customElements.get('ogg-location-selector')) {
    customElements.define(
      'ogg-location-selector',
      class OggLocationSelector extends HTMLElement {
        connectedCallback() {
          this.labelEl = this.querySelector('[data-ogg-label]');
          this.details = this.querySelector('details');
          this.options = Array.prototype.slice.call(this.querySelectorAll('[data-ogg-gid]'));
          this._onChange = this.render.bind(this);
          this._onClick = this.onOptionClick.bind(this);
          this.options.forEach(function (btn) { btn.addEventListener('click', this._onClick); }, this);
          document.addEventListener(EVT, this._onChange);
          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener(EVT, this._onChange);
        }

        onOptionClick(e) {
          var gid = e.currentTarget.getAttribute('data-ogg-gid');
          var self = this;
          window.OggLocation.set(gid).then(function () {
            if (self.details) self.details.open = false;
          });
        }

        render() {
          var loc = window.OggLocation.get();
          if (this.labelEl) this.labelEl.textContent = loc ? loc.name : 'Select location';
          this.classList.toggle('ogg-location-selector--unset', !loc);
          this.options.forEach(function (btn) {
            var active = loc && btn.getAttribute('data-ogg-gid') === loc.gid;
            btn.classList.toggle('is-active', active);
            btn.setAttribute('aria-current', active ? 'true' : 'false');
          });
        }
      }
    );
  }

  /* ------------------------------------------------------------------ *
   * PDP availability — INFORMATIONAL ONLY.
   *
   * Shows stock amounts for EVERY location from the product's
   * custom.ogg_location_stock metafield, independent of which store (if any) is
   * selected. It NEVER disables the add-to-cart button: items can be added from
   * any location. The button's enabled/disabled state comes solely from
   * Shopify's native sold-out logic in buy-buttons.liquid.
   * ------------------------------------------------------------------ */
  if (!customElements.get('ogg-location-availability')) {
    customElements.define(
      'ogg-location-availability',
      class OggLocationAvailability extends HTMLElement {
        connectedCallback() {
          this.statusEl = this.querySelector('[data-ogg-status]');
          this.stock = this.parseStock(this.getAttribute('data-stock'));
          this._onChange = this.render.bind(this);
          document.addEventListener(EVT, this._onChange);
          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener(EVT, this._onChange);
        }

        parseStock(raw) {
          if (!raw) return {};
          try {
            var p = JSON.parse(raw);
            return p && typeof p === 'object' ? p : {};
          } catch (e) {
            return {};
          }
        }

        setStatus(text, modifier) {
          if (!this.statusEl) return;
          this.statusEl.textContent = text;
          this.statusEl.className = 'ogg-stock-location__status' + (modifier ? ' ' + modifier : '');
        }

        render() {
          // Set false to hide exact numbers (shows "in stock" / "out of stock" only).
          var SHOW_COUNTS = true;

          var stock = this.stock;
          var locs = (window.OggLocation && window.OggLocation.locations) || LOCATIONS;
          var gids = Object.keys(locs);

          var hasData = gids.some(function (gid) {
            var q = stock[gid];
            return q !== undefined && q !== null;
          });

          // Product not synced yet (no metafield) — show nothing, never block.
          if (!hasData) {
            this.setStatus('');
            return;
          }

          var parts = gids.map(function (gid) {
            var name = locs[gid];
            var q = stock[gid];
            if (q === undefined || q === null) return name + ': n/a';
            q = Number(q);
            if (q > 0) return SHOW_COUNTS ? name + ': ' + q + ' in stock' : name + ': in stock';
            return name + ': out of stock';
          });

          this.setStatus(parts.join('\n'));
                }
      }
    );
  }

  /* ------------------------------------------------------------------ *
   * Collection-card availability — NEUTRALISED.
   *
   * Location no longer gates grid quick-add. This element now only respects the
   * native (globally sold-out) state Liquid already rendered and otherwise shows
   * the normal "Add to cart" label, so cards are addable from any location.
   * ------------------------------------------------------------------ */
  if (!customElements.get('ogg-card-availability')) {
    customElements.define(
      'ogg-card-availability',
      class OggCardAvailability extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('.quick-add__submit');
          this.labelEl = this.button ? this.button.querySelector('[data-ogg-card-label]') : null;
          // Capture native sold-out ONCE so we never re-enable a genuinely
          // sold-out product.
          this.nativeDisabled = this.button ? this.button.hasAttribute('disabled') : false;
          this.addLabel = this.getAttribute('data-add-label') || 'Add to cart';
          this.render();
        }

        setButton(blocked, label) {
          if (!this.button) return;
          var finalDisabled = this.nativeDisabled || blocked;
          this.button.disabled = finalDisabled;
          if (finalDisabled) this.button.setAttribute('disabled', 'disabled');
          else this.button.removeAttribute('disabled');
          if (this.labelEl && !this.nativeDisabled && label) this.labelEl.textContent = label;
        }

        render() {
          // Native sold-out stays blocked (and keeps Liquid's "Sold out" label);
          // everything else is addable regardless of location.
          if (this.nativeDisabled) {
            this.setButton(true);
            return;
          }
          this.setButton(false, this.addLabel);
        }
      }
    );
  }

  /* ------------------------------------------------------------------ *
   * Resolve the initial location once the DOM is ready.
   * ------------------------------------------------------------------ */
  function init() {
    // Prefer the server-rendered cart attribute (authoritative, flows to order);
    // fall back to localStorage (survives empty carts / fresh loads).
    var selectorEl = document.querySelector('ogg-location-selector');
    var serverGid = selectorEl ? (selectorEl.getAttribute('data-ogg-current-gid') || '').trim() : '';

    if (serverGid && LOCATIONS[serverGid]) {
      current = { gid: serverGid, name: LOCATIONS[serverGid] };
      lsSet(current.gid, current.name); // keep localStorage in step with the cart
    } else {
      var ls = lsGet();
      current = ls && LOCATIONS[ls.gid] ? { gid: ls.gid, name: LOCATIONS[ls.gid] } : null;
    }
    broadcast();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();